import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Tag, Clock, Copy, Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CouponWidget = () => {
  const [coupons, setCoupons] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedCoupons();
  }, []);

  useEffect(() => {
    // Rotate through coupons if multiple
    if (coupons.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % coupons.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [coupons.length]);

  useEffect(() => {
    // Update countdown timer
    if (coupons.length > 0 && coupons[currentIndex]?.valid_until) {
      const updateTimer = () => {
        const now = new Date();
        const expiry = new Date(coupons[currentIndex].valid_until);
        const diff = expiry - now;

        if (diff <= 0) {
          setTimeLeft(null);
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (days > 0) {
          setTimeLeft(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setTimeLeft(`${minutes}m ${seconds}s`);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setTimeLeft(null);
    }
  }, [coupons, currentIndex]);

  const fetchFeaturedCoupons = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coupons/featured`);
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
        
        // Check if user dismissed this session
        const dismissed = sessionStorage.getItem('coupon_widget_dismissed');
        if (dismissed) {
          setIsVisible(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch featured coupons:', error);
    }
  };

  const handleCopyCode = () => {
    if (coupons[currentIndex]) {
      navigator.clipboard.writeText(coupons[currentIndex].code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('coupon_widget_dismissed', 'true');
  };

  const handleGoToChallenges = () => {
    navigate('/challenges');
  };

  // Don't render if no coupons or dismissed
  if (!isVisible || coupons.length === 0) {
    return null;
  }

  const currentCoupon = coupons[currentIndex];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 20 }}
        className="fixed bottom-20 right-4 z-50 max-w-xs"
        data-testid="coupon-widget"
      >
        <div className="relative">
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 w-6 h-6 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors z-10"
            data-testid="coupon-dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Main widget */}
          <div className="bg-[hsl(220,20%,7%)]/95 backdrop-blur-lg rounded-xl shadow-2xl border border-blue-500/30 overflow-hidden">
            {/* Header with animation */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-4 py-2">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-white animate-bounce" />
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  Special Offer!
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Discount display */}
              <div className="text-center mb-3">
                <p className="text-3xl font-bold text-white">
                  {currentCoupon.discount_percent > 0 
                    ? `${currentCoupon.discount_percent}% OFF`
                    : `$${currentCoupon.discount_amount} OFF`
                  }
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {currentCoupon.display_message}
                </p>
              </div>

              {/* Coupon code */}
              <div 
                onClick={handleCopyCode}
                className="bg-black/40 rounded-lg px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-black/60 transition-colors group"
                data-testid="coupon-code-copy"
              >
                <div>
                  <p className="text-xs text-gray-400 mb-1">USE CODE:</p>
                  <p className="text-xl font-mono font-bold text-cyan-400 tracking-wider">
                    {currentCoupon.code}
                  </p>
                </div>
                <div className="text-gray-400 group-hover:text-white transition-colors">
                  {copied ? (
                    <Check className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </div>
              </div>

              {/* Countdown timer */}
              {timeLeft && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-red-400 animate-pulse" />
                  <span className="text-red-400">Expires in:</span>
                  <span className="font-mono font-bold text-white">{timeLeft}</span>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={handleGoToChallenges}
                className="mt-4 w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] shadow-lg shadow-blue-500/20"
                data-testid="coupon-cta"
              >
                Claim Now
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Multiple coupons indicator */}
              {coupons.length > 1 && (
                <div className="flex justify-center gap-1 mt-3">
                  {coupons.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentIndex ? 'bg-cyan-400' : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CouponWidget;
